import React, { useState, useMemo } from 'react';
import { StripeLinkedAccountsResponse, StripeLinkedAccount, QboConnection, QboAccount, UserMetadataResponse } from "../../../api/types";
import { useIsMobile } from '../../../utils/hooks';
import { useNavigate } from 'react-router-dom';
import { Landmark, MoreHorizontal, Plus, RefreshCw, List, Trash2 } from 'lucide-react';
import stripeApi from '../../../api/stripeApi';
import settingsApi from '../../../api/settingsApi';
import { getStripe } from '../../../api/stripeClient';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';

export interface AccountsProps {
    stripeData?: StripeLinkedAccountsResponse;
    stripeError?: string;
    qboData?: QboAccount[] | QboConnection[];
    userMetadata?: UserMetadataResponse;
    handleStripeLink: () => void;
    handleQboLink: () => void;
    refetchData?: () => void;
}

export default function Accounts({ stripeData, stripeError, qboData, userMetadata, handleStripeLink, handleQboLink, refetchData }: AccountsProps) {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const handleReconnect = async (accountId: string) => {
        try {
            const data = await stripeApi.reconnectAccount(accountId);
            const client_secret = data.client_secret;

            if (!client_secret) {
                console.error("No client_secret returned from reconnect");
                alert("Failed to initialize reconnect flow.");
                return;
            }

            const stripe = await getStripe();
            if (!stripe) {
                console.error("Stripe failed to initialize.");
                return;
            }

            const result = await stripe.collectFinancialConnectionsAccounts({
                clientSecret: client_secret
            });

            if (result.error) {
                console.error("Stripe reconnect error:", result.error.message);
                alert("Reconnect failed. Please try again.");
            } else {
                // Success
                if (refetchData) await refetchData();
                alert("Account reconnected successfully.");
            }
        } catch (error) {
            console.error("Failed to start reconnect", error);
            alert("Failed to start reconnect process. Please try again.");
        }
        setOpenMenuId(null);
    };

    const handleRefresh = async () => {
        if (openMenuId) {
            try {
                await stripeApi.syncAccountTransactions(openMenuId);
                if (refetchData) await refetchData();
            } catch (error: any) {
                console.error("Failed to sync account transactions", error);
                // Smart Refresh Logic: Check for auth errors
                if (error.response?.status === 401 || error.response?.status === 403 || error.message?.includes('reconnect')) {
                    if (window.confirm("Connection expired. Do you want to reconnect now?")) {
                        handleReconnect(openMenuId);
                        return;
                    }
                }
                alert(getUserFriendlyErrorMessage(error, "Failed to sync transactions. Please try again."));
            }
        }
        setOpenMenuId(null);
    };

    const handleShowTransactions = (accountId: string) => {
        // Navigate to Ledger with financial_account_id filter
        navigate(`/ledger?financial_account_id=${accountId}`);
        setOpenMenuId(null);
    };

    const handleDelete = async (accountId: string) => {
        if (window.confirm("Are you sure you want to disconnect this account?")) {
            try {
                await stripeApi.disconnect(accountId);
                if (refetchData) await refetchData();
            } catch (error) {
                console.error("Failed to disconnect account", error);
                alert("Failed to disconnect account. Please try again.");
            }
        }
        setOpenMenuId(null);
    };

    const handleDisconnectQbo = async () => {
        if (window.confirm("Are you sure you want to disconnect Quickbooks? This will remove all associated data.")) {
            try {
                await settingsApi.disconnectQbo();
                // We need to refresh the parent data to reflect the disconnection
                if (refetchData) await refetchData();
                alert("QuickBooks disconnected successfully.");
            } catch (error) {
                console.error("Failed to disconnect QBO", error);
                alert("Failed to disconnect QuickBooks. Please try again.");
            }
        }
        setOpenMenuId(null);
    };

    const [isVerified] = useState(() => {
        return sessionStorage.getItem('approval_status') === 'true';
    });

    // === COMPATIBILITY LAYER ===
    // We now prefer userMetadata for connection status and details
    const normalizedQboData: QboConnection[] = useMemo(() => {
        // If we have real metadata about the connection, use it
        if (userMetadata?.connections?.linked_qbo && userMetadata.qbo_metadata) {
            const { company_name, company_email, fiscal_year_period, realm_id, source, provider, connected_at, last_sync_at } = userMetadata.qbo_metadata;
            const { qbo_last_sync, qbo_connected_at } = userMetadata.connections;
            const isDesktopConnector =
                source === 'qbdesktop' ||
                provider === 'quickbooks_desktop' ||
                String(realm_id || '').startsWith('qbdesktop:');

            return [{
                _id: "qbo_metadata_id", // Placeholder as it comes from metadata
                user_id: userMetadata.user_id,
                company_name: company_name || "QuickBooks Account",
                company_email: company_email || (isDesktopConnector ? "QuickBooks Desktop Connector" : "QuickBooks Online"),
                is_connected: true,
                last_sync_at: last_sync_at || qbo_last_sync || new Date().toISOString(),
                fiscal_year_period: fiscal_year_period || "N/A",
                source,
                provider,
                realm_id,
                connected_at: connected_at || qbo_connected_at || qbo_last_sync || null,
            }];
        }

        // Fallback to old qboData logic if metadata isn't ready but legacy data exists (optional back-compat)
        if (!qboData || qboData.length === 0) return [];

        if ('company_name' in qboData[0]) {
            return qboData as QboConnection[];
        }

        // Legacy/Invalid data - treat as empty to avoid showing fake accounts
        return [];
    }, [qboData, userMetadata]);

    const formatDate = (dateString?: string, timeOnlyIfToday = false) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);

        if (timeOnlyIfToday) {
            const today = new Date();
            if (date.toDateString() === today.toDateString()) {
                const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                return `${timeStr} Today`;
            }
        }
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'numeric', year: 'numeric' });
    };

    const getQboProviderLabel = (company: QboConnection) => {
        const source = (company as any).source;
        const provider = (company as any).provider;
        const realmId = (company as any).realm_id;
        if (source === 'qbdesktop' || provider === 'quickbooks_desktop' || String(realmId || '').startsWith('qbdesktop:')) {
            return 'QuickBooks Desktop';
        }
        return 'QuickBooks Online';
    };

    const activeStripeAccounts = stripeData?.linked_accounts?.filter(a => a.status === 'active') ?? [];
    const hasStripe = activeStripeAccounts.length > 0;
    const hasQbo = normalizedQboData.length > 0;
    const hasStripeError = Boolean(stripeError);

    // Check if TOTAL empty (to show the initial empty state page)
    const isTotalEmpty = !hasStripe && !hasQbo && !hasStripeError;

    return (
        // 1. Root: h-full + overflow-hidden to lock the page height. flex-col to organize children.
        <div className={`flex flex-col w-full h-full overflow-hidden ${isMobile ? '' : 'min-w-[700px]'}`}>
            {isTotalEmpty ? (
                /* === TOTAL EMPTY STATE (Initial View) === */
                <div className="w-full mx-auto flex-1 flex flex-col gap-6 min-h-0 overflow-y-auto">
                    <div className="w-full flex-1 bg-white border border-gray-200 rounded-[20px] flex flex-col items-center justify-center gap-6 text-center shadow-sm min-h-[300px]">
                        <div className="w-full max-w-[564px] flex flex-col gap-1 px-4">
                            <h2 className="font-sans font-semibold text-lg text-gray-900">1. Connect your Quickbooks</h2>
                            <p className="font-sans font-normal text-sm text-gray-500">Cash flow and margin will be available once your accounts are connected.</p>
                        </div>
                        <button onClick={handleQboLink} disabled={!isVerified} className={`w-[240px] h-[48px] shrink-0 flex items-center justify-center gap-2 rounded-lg transition-colors ${isVerified ? 'bg-[#5B5DE4] hover:bg-gray-800 text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}>
                            <span className="font-medium text-[16px]">Connect to Quickbooks</span>
                        </button>
                    </div>
                    <div className="w-full flex-1 bg-white border border-gray-200 rounded-[20px] flex flex-col items-center justify-center gap-6 text-center shadow-sm min-h-[300px]">
                        <div className="w-full max-w-[564px] flex flex-col gap-1 px-4">
                            <h2 className="font-sans font-semibold text-lg text-gray-900">2. Add your first account</h2>
                            <p className="font-sans font-normal text-sm text-gray-500">Cash flow and margin will be available once your accounts are connected.</p>
                        </div>
                        <button onClick={handleStripeLink} disabled={!isVerified} className={`w-[240px] h-[48px] shrink-0 flex items-center justify-center gap-2 rounded-lg transition-colors ${isVerified ? 'bg-[#5B5DE4] hover:bg-gray-800 text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}>
                            <span className="font-medium text-[16px]">Connect a Bank Account</span>
                        </button>
                    </div>
                </div>
            ) : (
                /* === SPLIT VIEW (If at least one is connected) === */
                <div className="w-full mx-auto flex-1 flex flex-col gap-6 min-h-0 overflow-hidden">

                    {/* ==========================
                        BOX 1: QBO ACCOUNTING
                       ========================== */}
                    <div className="w-full flex-none bg-white border border-gray-200 rounded-[20px] flex flex-col shadow-sm overflow-hidden relative">

                        {hasQbo ? (
                            <>
                                {/* Header */}
                                <div className="w-full flex justify-between items-center shrink-0 p-6 pb-0">
                                    <h2 className="font-sans font-semibold text-base text-gray-900">Accounting</h2>
                                </div>
                                {/* List */}
                                <div className="flex flex-col gap-0 overflow-y-auto flex-1 p-6 pt-6">
                                    {normalizedQboData.map((company) => (
                                        <div key={company._id} className="w-full min-h-[78px] shrink-0 flex items-center justify-between border-b border-gray-100 last:border-0 px-2 hover:bg-gray-50 rounded-lg transition-colors py-3">

                                            {/* 1. Logo + Company Info Group (Total Width: 238px) */}
                                            <div className="flex items-start gap-[12px] w-[238px] shrink-0">

                                                {/* Logo (74x74) */}
                                                <div className="w-[74px] h-[74px] rounded-full bg-white flex items-center justify-center border border-gray-100 shrink-0 shadow-sm overflow-hidden">
                                                    <img src="images/icons/quickbooks-2.svg" alt="Quickbooks" className="w-10 h-10 object-contain" />
                                                </div>

                                                {/* Text Info */}
                                                <div className="flex flex-col h-[74px] justify-between">
                                                    <div className="font-sans text-[16px] font-semibold text-gray-900 leading-tight truncate w-full">
                                                        {company.company_name}
                                                    </div>

                                                    <div className="flex flex-col gap-1">
                                                        <span className="font-sans text-[14px] text-gray-500 leading-tight">{getQboProviderLabel(company)}</span>

                                                        {company.is_connected && (
                                                            <span className="w-[88px] h-[25px] flex items-center justify-center bg-green-50 text-green-700 text-[12px] font-medium rounded-[32px] border border-green-100">
                                                                Connected
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 2. Connected as (Width: 226px) */}
                                            <div className="w-[226px] shrink-0 flex flex-col justify-center gap-1">
                                                <div className="font-sans font-medium text-sm text-gray-900">Connected via</div>
                                                <div className="font-sans text-sm text-gray-500 truncate w-full" title={company.company_email}>
                                                    {company.company_email}
                                                </div>
                                            </div>

                                            {/* 3. Accounting period (Width: 141px) */}
                                            <div className="w-[141px] shrink-0 flex flex-col justify-center gap-1">
                                                <div className="font-sans font-medium text-sm text-gray-900">Connected at</div>
                                                <div className="font-sans text-sm text-gray-500">
                                                    {formatDate((company as any).connected_at, true)}
                                                </div>
                                            </div>

                                            {/* 4. Last sync (Width: 93px) */}
                                            <div className="w-[93px] shrink-0 flex flex-col justify-center gap-1">
                                                <div className="font-sans font-medium text-sm text-gray-900">Last sync</div>
                                                <div className="font-sans text-sm text-gray-500 whitespace-nowrap">
                                                    {formatDate(company.last_sync_at, true)}
                                                </div>
                                            </div>

                                            {/* 5. Menu Icon (24px x 24px) */}
                                            <div className="relative">
                                                <button
                                                    onClick={() => setOpenMenuId(openMenuId === company._id ? null : company._id)}
                                                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                                                >
                                                    <MoreHorizontal size={20} />
                                                </button>

                                                {openMenuId === company._id && (
                                                    <div className="absolute right-0 top-8 w-48 bg-white rounded-md shadow-lg border border-gray-100 z-10 py-1">
                                                        <button
                                                            onClick={handleDisconnectQbo}
                                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                                                        >
                                                            <Trash2 size={14} />
                                                            Disconnect
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            /* Empty State for Box 2 (QBO) */
                            <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center p-8 min-h-[250px]">
                                <div className="w-full flex-col gap-1 px-4 mb-2">
                                    <h2 className="font-sans font-semibold text-lg text-gray-900">1. Connect your Quickbooks</h2>
                                    <p className="font-sans font-normal text-sm text-gray-500">Cash flow and margin will be available once your accounts are connected.</p>
                                </div>
                                <button onClick={handleQboLink} disabled={!isVerified} className={`w-[240px] h-[48px] shrink-0 flex items-center justify-center gap-2 rounded-md transition-colors ${isVerified ? 'bg-[#1F2937] hover:bg-gray-800 text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}>
                                    <img src="images/icons/quickbooks-2.svg" alt="QB" className="w-4 h-4 object-contain" />
                                    <span className="font-medium text-sm">Connect to Quickbooks</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ==========================
                        BOX 2: STRIPE BANK ACCOUNTS
                       ========================== */}
                    <div className="w-full flex-1 min-h-0 bg-white border border-gray-200 rounded-[20px] flex flex-col shadow-sm overflow-hidden relative">

                        {hasStripeError ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center p-8">
                                <div className="w-full max-w-[564px] flex flex-col gap-2 px-4">
                                    <h2 className="font-sans font-semibold text-lg text-gray-900">Bank accounts unavailable</h2>
                                    <p className="font-sans font-normal text-sm text-gray-500">
                                        We could not load connected bank accounts for this business.
                                    </p>
                                    <p className="font-sans font-normal text-sm text-red-600">
                                        {stripeError}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => refetchData?.()}
                                        className="h-[44px] px-5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Retry
                                    </button>
                                    <button
                                        onClick={handleStripeLink}
                                        className="h-[44px] px-5 rounded-md bg-[#1F2937] hover:bg-gray-800 text-sm font-medium text-white transition-colors"
                                    >
                                        Reconnect account
                                    </button>
                                </div>
                            </div>
                        ) : hasStripe ? (
                            <>
                                {/* Header */}
                                <div className="w-full flex justify-between items-center shrink-0 p-6 pb-0">
                                    <h2 className="font-sans font-semibold text-base text-gray-900">Bank Accounts</h2>
                                    <button onClick={handleStripeLink} className="flex items-center gap-2 bg-[#1F2937] hover:bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                                        Add accounts <Plus size={16} />
                                    </button>
                                </div>
                                {/* List */}
                                <div className="flex flex-col gap-0 overflow-y-auto flex-1 p-6 pt-6">
                                    {activeStripeAccounts
                                        .map((account: StripeLinkedAccount) => {
                                            const accountId = account._id || account.financial_account_id;
                                            return (
                                                <div key={accountId} className="w-full h-[74px] shrink-0 flex items-center justify-between border-b border-gray-100 last:border-0 px-2 hover:bg-gray-50 rounded-lg transition-colors">
                                                    <div className="flex items-center gap-4 min-w-[300px]">
                                                        <div className="w-[74px] h-[74px] rounded-full bg-white flex items-center justify-center border border-gray-100 shrink-0 shadow-sm">
                                                            <Landmark className="text-blue-600 w-8 h-8" />
                                                        </div>
                                                        <div>
                                                            <div className="font-sans font-semibold text-base text-gray-900">
                                                                {account.display_name || account.institution_name}
                                                            </div>
                                                            <div className="font-sans text-sm text-gray-500">
                                                                {account.last4 ? `•••• ${account.last4}` : "•••• 0000"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="min-w-[150px]">
                                                        <div className="font-sans font-bold text-lg text-gray-900">
                                                            {(account.current_balance ?? account.balance_state?.derived_current ?? account.balance_state?.baseline_current ?? account.latest_balance?.current_usd ?? account.latest_balance?.available_balance ?? account.latest_balance?.current_balance ?? 0).toLocaleString('en-US', { style: 'currency', currency: account.latest_balance?.currency || 'USD' })}
                                                        </div>
                                                    </div>
                                                    {/* <div className="min-w-[150px]">
                                                        <div className="font-sans font-medium text-sm text-gray-900">Login valid until</div>
                                                        <div className="font-sans text-sm text-gray-500">
                                                            {formatDate(account.consent_expires_at) || "N/A"}
                                                        </div>
                                                    </div> */}
                                                    <div className="min-w-[150px]">
                                                        <div className="font-sans font-medium text-sm text-gray-900">Last update</div>
                                                        <div className="font-sans text-sm text-gray-500">
                                                            {formatDate(account.stripe_synced_at || account.stripe_transaction_synced_at || account.stripe_balance_synced_at || account.latest_balance?.fetched_at, true)}
                                                        </div>
                                                    </div>
                                                    <div className="relative">
                                                        <button
                                                            onClick={() => setOpenMenuId(openMenuId === accountId ? null : accountId)}
                                                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                                        >
                                                            <MoreHorizontal size={20} />
                                                        </button>

                                                        {openMenuId === accountId && (
                                                            <div className="absolute right-0 top-12 w-48 bg-white rounded-md shadow-lg border border-gray-100 z-10 py-1">
                                                                <button
                                                                    onClick={handleRefresh}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                                                                >
                                                                    <RefreshCw size={14} />
                                                                    Sync transactions
                                                                </button>
                                                                {/* <button
                                                                    onClick={() => handleReconnect(accountId)}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                                                                >
                                                                    <RefreshCw size={14} className="text-blue-600" />
                                                                    Reconnect
                                                                </button> */}
                                                                <button
                                                                    onClick={() => handleShowTransactions(accountId)}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                                                                >
                                                                    <List size={14} />
                                                                    Show transactions
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(accountId)}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                                                                >
                                                                    <Trash2 size={14} />
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </>
                        ) : (
                            /* Empty State for Box 1 (Stripe) */
                            <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center">
                                <div className="w-full flex-col gap-1 px-4 mb-2">
                                    <h2 className="font-sans font-semibold text-lg text-gray-900">2. Add your first account</h2>
                                    <p className="font-sans font-normal text-sm text-gray-500">Cash flow and margin will be available once your accounts are connected.</p>
                                </div>
                                <button onClick={handleStripeLink} disabled={!isVerified} className={`w-[240px] h-[48px] shrink-0 flex items-center justify-center gap-2 rounded-md transition-colors ${isVerified ? 'bg-[#1F2937] hover:bg-gray-800 text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}>
                                    <Landmark className="w-4 h-4" />
                                    <span className="font-medium text-sm">Connect a Bank Account</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
